import React, { useCallback, useEffect, useState } from "react";
import { Bot, Pause, Play, Loader2, RefreshCcw } from "lucide-react";
import { toast } from "sonner";
import { api } from "../lib/api";

const fmt = (iso) => (iso ? new Date(iso).toLocaleString("fr-FR", { dateStyle: "short", timeStyle: "short" }) : "—");

function Stat({ label, value, testid }) {
  return (
    <div>
      <div className="text-[11px] uppercase tracking-wider text-norea-textMuted">{label}</div>
      <div className="text-sm font-medium mt-0.5" data-testid={testid}>{value}</div>
    </div>
  );
}

export default function AgentBdcCard() {
  const [s, setS] = useState(null);
  const [busy, setBusy] = useState(false);
  const load = useCallback(() => api.get("/agent-bdc/status").then((r) => setS(r.data)).catch(() => setS({ error: true })), []);
  useEffect(() => { load(); }, [load]);
  const toggle = async () => {
    setBusy(true);
    try { const r = await api.post("/agent-bdc/pause", { paused: !s.paused }); setS(r.data); toast.success(r.data.paused ? "Agent mis en pause" : "Agent relancé"); }
    catch { toast.error("Action impossible"); }
    finally { setBusy(false); }
  };
  if (!s) return <div className="h-32 bg-white border border-norea-border rounded-lg animate-pulse" />;
  const active = s.running && !s.paused;
  return (
    <div className="bg-white border border-norea-border rounded-lg p-6" data-testid="agent-bdc-card">
      <div className="flex items-start justify-between mb-4">
        <div className="flex items-start gap-3">
          <div className="w-10 h-10 rounded-md bg-norea-surfaceHover flex items-center justify-center text-norea-primary"><Bot className="w-5 h-5" /></div>
          <div>
            <h3 className="font-heading text-xl font-medium">Agent « Traitement BDC »</h3>
            <div className="mt-1 text-xs flex items-center gap-1.5" data-testid="agent-bdc-state">
              <span className={`w-2 h-2 rounded-full ${active && !s.gmail_cooldown ? "bg-emerald-500" : "bg-amber-500"}`} />
              {active ? (s.gmail_cooldown ? "Actif · en attente : Gmail limite temporairement l'accès (voir ci-dessous)" : `Actif · lecture de Gmail toutes les ${s.interval_min} min`) : s.paused ? "En pause" : "Arrêté"}
            </div>
          </div>
        </div>
        <div className="flex gap-2">
          <button onClick={load} className="btn-secondary text-xs" data-testid="agent-bdc-refresh"><RefreshCcw className="w-3.5 h-3.5" /> Actualiser</button>
          <button onClick={toggle} disabled={busy || s.error} className="btn-secondary text-xs" data-testid="agent-bdc-toggle">
            {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : s.paused ? <Play className="w-3.5 h-3.5" /> : <Pause className="w-3.5 h-3.5" />} {s.paused ? "Reprendre" : "Mettre en pause"}
          </button>
        </div>
      </div>
      <p className="text-sm text-norea-textMuted mb-4">Crée les dossiers à partir des bons de commande reçus dans Gmail (libellé « 1/ Dossiers à traiter »). La pause n'arrête pas le rattachement des emails aux dossiers.</p>
      <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
        <Stat label="Dernier passage" value={fmt(s.last_run_at)} testid="agent-bdc-last-run" />
        <Stat label="Prochain passage" value={s.paused ? "—" : fmt(s.next_run_at)} testid="agent-bdc-next-run" />
        <Stat label="Traités au dernier passage" value={s.last_processed ?? "—"} testid="agent-bdc-last-processed" />
        <Stat label="Traités aujourd'hui" value={s.processed_today ?? 0} testid="agent-bdc-today" />
        <Stat label="Total traités" value={s.processed_total ?? 0} testid="agent-bdc-total" />
      </div>
      {s.last_error && <div className="mt-3 text-xs text-status-erreurText" data-testid="agent-bdc-error">Dernière erreur : {s.last_error}</div>}
    </div>
  );
}
